import { AchFilterModelDto } from '@treasury/api/channel';
import { DomainEntity } from '../shared';

export const defaultAchFilterDto: AchFilterModelDto = {
    accountNumber: '',
    accountUniqueId: undefined,
    achCompanyId: '',
    achCompanyName: '',
    achFilterUniqueId: undefined,
    allowType: '',
    accountAllowsAllCredits: false,
    accountAllowsAllDebits: false,
    createdDate: undefined,
    expirationDate: undefined,
    filterRuleName: '',
    fromAmount: 0,
    remarks: '',
    routingNumber: '',
    secCode: '',
    status: '',
    toAmount: 0,
    transactionType: '',
};

export class AchFilter extends DomainEntity<AchFilterModelDto> {
    public createDefault(): this {
        return new AchFilter({ ...defaultAchFilterDto }) as this;
    }

    public get achFilterUniqueId() {
        return this.dto.achFilterUniqueId;
    }

    public get accountNumber() {
        return this.dto.accountNumber ?? '';
    }

    public get accountNickname() {
        return this.dto.accountNickname;
    }

    public get accountUniqueId() {
        return this.dto.accountUniqueId;
    }

    public get achCompanyId() {
        return this.dto.achCompanyId ?? '';
    }

    public get achCompanyName() {
        return this.dto.achCompanyName ?? '';
    }

    public get allowType() {
        return this.dto.allowType ?? '';
    }

    public get accountAllowsAllCredits() {
        return this.dto.accountAllowsAllCredits ?? false;
    }

    public get accountAllowsAllDebits() {
        return this.dto.accountAllowsAllDebits ?? false;
    }

    public get createdDate() {
        return this.dto.createdDate;
    }

    public get expirationDate() {
        return this.dto.expirationDate;
    }

    public get filterRuleName() {
        return this.dto.filterRuleName ?? '';
    }

    public get fromAmount() {
        return this.dto.fromAmount ?? 0;
    }

    public get toAmount() {
        return this.dto.toAmount ?? 0;
    }

    public get remarks() {
        return this.dto.remarks ?? '';
    }

    public get routingNumber() {
        return this.dto.routingNumber ?? '';
    }

    public get secCode() {
        return this.dto.secCode ?? '';
    }

    public get status() {
        return this.dto.status ?? '';
    }

    public get transactionType() {
        return this.dto.transactionType ?? '';
    }

    public get displayAmount(): string {
        const from = this.fromAmount;
        const to = this.toAmount;
        if (from === 0 && to === 0) {
            return 'N/A';
        }
        if (from === to) {
            return `$${from.toFixed(2)}`;
        }
        return `$${from.toFixed(2)} - $${to.toFixed(2)}`;
    }

    public get transactionTypeDisplay(): string {
        const txnType = this.transactionType;
        const { allowType } = this;
        if (!txnType) {
            return 'N/A';
        }
        if (!allowType) {
            return txnType;
        }
        const suffix = allowType === 'Allow' ? 'Allowed' : 'Not Allowed';
        return `${txnType} ${suffix}`;
    }

    public get displayExpirationDate(): string {
        const date = this.expirationDate;
        if (!date) {
            return 'N/A';
        }
        const d = new Date(date);
        if (isNaN(d.getTime())) {
            return 'N/A';
        }
        return d.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        });
    }

    public get accountMasterSettings(): string {
        const debits = this.accountAllowsAllDebits ? 'Allowed' : 'Not Allowed';
        const credits = this.accountAllowsAllCredits ? 'Allowed' : 'Not Allowed';
        return `Debits ${debits} / Credits ${credits}`;
    }

    public get isPending() {
        return this.status.toLowerCase() === 'pending approval';
    }

    public get isApproved() {
        return this.status.toLowerCase() === 'approved';
    }

    public get isActive() {
        return this.status.toLowerCase() === 'active';
    }

    public get isFailed() {
        return this.status.toLowerCase() === 'failed';
    }

    public get statusBadgeType(): string {
        if (this.isActive) {
            return 'success';
        }
        if (this.isFailed) {
            return 'error';
        }
        if (this.isPending) {
            return 'warning';
        }
        return 'inactive';
    }
}
